import { needsOf } from "./catalog";
import type { Setting } from "./types";

/**
 * ═══ LES LIGNES D'UNE FAMILLE ═══
 *
 * Une famille affichait un réglage par ligne. Trois cas en faisaient beaucoup trop, et
 * ils se reconnaissent dans le catalogue sans rien y déclarer de plus :
 *
 *   — UN INTERRUPTEUR ET SON TEXTE. « Bouton de retour à l'accueil » puis, une ligne plus
 *     bas, « Libellé du bouton » qui n'existe que si le bouton est affiché. Le parcours
 *     le faisait déjà (le nom d'une étape vit sur sa ligne) : le texte passe SOUS son
 *     interrupteur, dans le même bloc, et disparaît avec lui sans note « masqué ».
 *   — UNE LISTE DE CASES. Les onze lignes du récapitulatif de la confirmation sont onze
 *     interrupteurs à la suite : une grille de pastilles les tient en trois lignes.
 *   — UN INTITULÉ ET SON TEXTE. Le pied de page en a quatre paires : côte à côte.
 *
 * ⚠️ LA RELATION EST LUE DANS `needs`, PAS DÉCLARÉE UNE SECONDE FOIS. Un texte qui dépend
 * d'un seul interrupteur de sa famille lui appartient. Au-delà de deux textes, on garde
 * l'affichage d'origine : un interrupteur qui gouverne dix réglages (le pied de page)
 * est l'entrée d'une famille, pas une ligne.
 */
export type Line =
  | { kind: "row"; setting: Setting }
  | { kind: "pair"; setting: Setting; children: Setting[] }
  | { kind: "chips"; section: string; settings: Setting[] }
  | { kind: "duo"; first: Setting; second: Setting };

/** Au-delà, l'interrupteur garde ses réglages en lignes à part. */
const MAX_CHILDREN = 2;
/** En dessous, une suite d'interrupteurs reste en lignes : la grille n'apporte rien. */
const MIN_CHIPS = 4;

const PROSE: ReadonlySet<Setting["type"]> = new Set(["text", "html"]);

/**
 * Les textes rattachés à chaque interrupteur de la famille.
 *
 * Calculé sur TOUS les réglages de la famille, masqués compris : c'est ce qui permet de
 * savoir qu'un texte endormi l'est par son propre interrupteur, et de taire la note.
 */
export function childrenBySwitch(
  settings: ReadonlyArray<Setting>,
): Map<string, Setting[]> {
  const out = new Map<string, Setting[]>();
  for (const master of settings) {
    if (master.type !== "boolean") continue;
    const children = settings.filter((s) => {
      if (!PROSE.has(s.type)) return false;
      const needs = needsOf(s);
      return needs.length === 1 && needs[0] === master.key;
    });
    if (children.length > 0 && children.length <= MAX_CHILDREN)
      out.set(master.key, children);
  }
  return out;
}

export function linesOf(
  shown: ReadonlyArray<Setting>,
  children: ReadonlyMap<string, ReadonlyArray<Setting>>,
): Line[] {
  const owned = new Set(
    [...children.values()].flatMap((list) => list.map((s) => s.key)),
  );
  const visible = new Set(shown.map((s) => s.key));
  const isPlainSwitch = (s: Setting | undefined, section: string) =>
    !!s &&
    s.type === "boolean" &&
    s.section === section &&
    !children.has(s.key) &&
    !owned.has(s.key);

  const lines: Line[] = [];
  for (let i = 0; i < shown.length; i += 1) {
    const setting = shown[i]!;
    if (owned.has(setting.key)) continue;

    const kids = children.get(setting.key);
    if (kids) {
      lines.push({
        kind: "pair",
        setting,
        children: kids.filter((k) => visible.has(k.key)),
      });
      continue;
    }

    if (setting.section && isPlainSwitch(setting, setting.section)) {
      let end = i;
      while (isPlainSwitch(shown[end], setting.section)) end += 1;
      if (end - i >= MIN_CHIPS) {
        lines.push({
          kind: "chips",
          section: setting.section,
          settings: shown.slice(i, end),
        });
        i = end - 1;
        continue;
      }
    }

    const next = shown[i + 1];
    if (
      setting.label === "Intitulé" &&
      setting.type === "text" &&
      next?.label === "Texte" &&
      next.type === "text" &&
      next.section === setting.section
    ) {
      lines.push({ kind: "duo", first: setting, second: next });
      i += 1;
      continue;
    }

    lines.push({ kind: "row", setting });
  }
  return lines;
}

/** Les clés qu'une ligne porte : pour la désigner depuis une recherche. */
export function keysOf(line: Line): string[] {
  switch (line.kind) {
    case "row":
      return [line.setting.key];
    case "pair":
      return [line.setting.key, ...line.children.map((c) => c.key)];
    case "chips":
      return line.settings.map((s) => s.key);
    case "duo":
      return [line.first.key, line.second.key];
  }
}

/** La section qu'une ligne ouvre, pour le sous-titre de la famille. */
export function sectionOf(line: Line): string | undefined {
  switch (line.kind) {
    case "row":
    case "pair":
      return line.setting.section;
    case "chips":
      return line.section;
    case "duo":
      return line.first.section;
  }
}

/**
 * Le libellé d'un texte rangé sous son interrupteur, sans répéter l'interrupteur.
 * « Présentiel · libellé » sous « Présentiel » se lit « Libellé ».
 */
export function childLabel(child: Setting, master: Setting): string {
  const prefix = `${master.label} · `;
  if (!child.label.startsWith(prefix)) return child.label;
  const rest = child.label.slice(prefix.length);
  return rest.charAt(0).toUpperCase() + rest.slice(1);
}
